// Shared PDF style utilities for OBRASUL-RJ / Obra Padrão documents.
// Provides high-contrast tokens, text-correction helpers, and safe HTML rendering.
//
// Design goals (canteiro de obra):
//   • Minimum 10pt fonts (readable at 1m with gloves)
//   • WCAG AAA contrast (≥7:1 for body text)
//   • Vibrant status colors that survive photocopy / low light
//   • Auto-fix common data-entry mistakes at render time (never mutates DB)

// ---------- Design tokens ----------
export const PDF_COLORS = {
  navy: "#1A237E",
  navyDark: "#0D1547",
  gold: "#D4A437",
  goldDark: "#A88A32",
  text: "#000000",
  textMuted: "#424242",
  textFaint: "#616161",
  border: "#BDBDBD",
  borderSoft: "#E0E0E0",
  bg: "#FFFFFF",
  bgAlt: "#F5F5F5",

  // Status palette — canteiro-safe
  statusConcluido: "#4CAF50",
  statusEmAndamento: "#2196F3",
  statusPendente: "#D32F2F",
  statusAtrasado: "#FF9800",
  statusCritico: "#9C27B0",

  // Status badge backgrounds (light) + foregrounds (dark) for WCAG AAA
  badgeConcluido: { bg: "#E8F5E9", color: "#1B5E20" },
  badgeEmAndamento: { bg: "#E3F2FD", color: "#0D47A1" },
  badgePendente: { bg: "#FFEBEE", color: "#B71C1C" },
  badgeAtrasado: { bg: "#FFF3E0", color: "#E65100" },
  badgeCritico: { bg: "#F3E5F5", color: "#6A1B9A" },
};

export const PDF_FONTS = {
  body: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
  heading: "'Arial Black', Arial, sans-serif",
  mono: "Consolas, 'Courier New', ui-monospace, monospace",
};

export const PDF_MARGINS = [15, 10, 15, 10]; // [top, right, bottom, left] mm — 1cm laterais + 1,5cm topo/base

// ---------- Text correction & safe rendering ----------
// Legacy ortho corrections applied to ALL rendered field data at PDF time.
// Never modifies the source database — only the rendered output.
export const TEXT_CORRECTIONS = [
  [/\bFTARA\b/gi, "ETAPA"],
  [/\bHIDROIATEAMENTO\b/gi, "HIDROJATEAMENTO"],
  [/\bLATERALA\b/gi, "LATERAL A"],
  [/\bFUNDOS\b/gi, "FUNDO"],
];

export const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const fixLegacyText = (s) => {
  if (!s) return s;
  let out = String(s);
  for (const [re, rep] of TEXT_CORRECTIONS) out = out.replace(re, rep);
  return out;
};

// Combines legacy-fix + HTML escape. Preferred wrapper for any DB-sourced string.
export const safeText = (s) => escapeHtml(fixLegacyText(s));

// ---------- Common style fragments ----------
// Root wrapper CSS shared across all PDFs. Modules extend with their own selectors.
export const pdfRootStyles = `
  * { box-sizing: border-box; }
  .pdf-root {
    font-family: ${PDF_FONTS.body};
    color: ${PDF_COLORS.text};
    width: 1080px;
    background: ${PDF_COLORS.bg};
    padding: 0 6px;
    line-height: 1.15;
  }
  .pdf-root .brand-bar { display: flex; height: 8px; margin-bottom: 18px; }
  .pdf-root .brand-bar .b1 { flex: 3; background: ${PDF_COLORS.navy}; }
  .pdf-root .brand-bar .b2 { flex: 1; background: ${PDF_COLORS.gold}; }
  .pdf-root .header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0 4px 14px; border-bottom: 2px solid ${PDF_COLORS.navy}; margin-bottom: 18px;
  }
  .pdf-root .header-left { display: flex; align-items: center; gap: 14px; }
  .pdf-root .brand-logo {
    width: 60px; height: 60px; object-fit: contain; border-radius: 6px;
    background: ${PDF_COLORS.navy};
  }
  .pdf-root .brand { font-size: 24px; font-weight: 900; color: ${PDF_COLORS.navy}; letter-spacing: 0.2px; font-family: ${PDF_FONTS.heading}; }
  .pdf-root .brand-sub { font-size: 12px; color: ${PDF_COLORS.textMuted}; margin-top: 3px; font-weight: 600; }
  .pdf-root .doc-meta { text-align: right; font-size: 11px; color: ${PDF_COLORS.textMuted}; }
  .pdf-root .doc-meta .num { font-size: 14px; color: ${PDF_COLORS.navy}; font-weight: 800; }

  .pdf-root h1 { font-size: 22px; font-weight: 900; color: ${PDF_COLORS.navy}; margin: 10px 4px 6px; font-family: ${PDF_FONTS.heading}; }
  .pdf-root h2 { font-size: 14px; font-weight: 800; color: ${PDF_COLORS.navy}; margin: 22px 4px 10px; padding-bottom: 5px; border-bottom: 2px solid ${PDF_COLORS.gold}; font-family: ${PDF_FONTS.heading}; }
  .pdf-root h3 { font-size: 13px; font-weight: 700; color: ${PDF_COLORS.navy}; margin: 14px 4px 8px; }
  .pdf-root .muted { color: ${PDF_COLORS.textFaint}; font-weight: 500; }

  .pdf-root .footer {
    margin: 22px 4px 0; padding-top: 12px; border-top: 2px solid ${PDF_COLORS.border};
    display: flex; justify-content: space-between; font-size: 10px; color: ${PDF_COLORS.textMuted}; font-weight: 600;
  }
  .pdf-root .footer b { color: ${PDF_COLORS.navy}; }
  .pdf-root .page-break { page-break-before: always; height: 0; }
`;

// ---------- Status helpers ----------
export function statusBadge(status, label) {
  const map = {
    pendente: PDF_COLORS.badgePendente,
    em_andamento: PDF_COLORS.badgeEmAndamento,
    concluido: PDF_COLORS.badgeConcluido,
    pago: PDF_COLORS.badgeConcluido,
    atrasado: PDF_COLORS.badgeAtrasado,
    vencido: PDF_COLORS.badgeAtrasado,
    cancelado: { bg: "#ECEFF1", color: "#37474F" },
    critico: PDF_COLORS.badgeCritico,
  };
  const cfg = map[status] || map.pendente;
  const text = label || status;
  return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:10px;font-weight:800;background:${cfg.bg};color:${cfg.color};text-transform:uppercase;letter-spacing:0.4px;font-family:${PDF_FONTS.body}">${escapeHtml(text)}</span>`;
}
